import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarPlus, Compass, ListChecks, Loader2, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import BuddyCorner from '@/components/common/BuddyCorner';
import { generatePlan } from '@/lib/premiumExams';

const ICON = {
  add_exam: CalendarPlus,
  map_portion: ListChecks,
  build_plan: Wand2,
  open_exams: ListChecks,
};

const BUDDY_LINE = {
  no_exam: "I don't want to guess what's in your exam — tell me which one it is and I'll plan every day for you.",
  exam_over: "We finished that one together. Give me the next date and I'll start counting down.",
  no_portion: "Tick the chapters your teacher gave you — then I'll know exactly what to study with you.",
  no_dates: "Once I know each paper's date, I can say what to do today, tomorrow and the day after.",
  no_plan: "The hard part is done. One tap and I'll lay out your days.",
  plan_complete: "Look at that — the whole portion, finished. I'm proud of you.",
};

/**
 * ExamModeGate — the pop-up that stops Exam Mode from starting on a chapter
 * that isn't even in the exam.
 *
 * Before this, switching Explore → Exam simply carried the explored chapter
 * across and started a guided session with a countdown clock, even when no exam
 * existed at all. Now the server decides whether Exam Mode may start, and this
 * dialog explains the one missing thing — with the buddy in the corner, because
 * being told "no" is exactly when a child most needs their friend in the room.
 */
export function ExamModeGate({ open, onOpenChange, readiness, onAddExam, onMapPortion, onReady, onBackToExplore }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const issue = readiness?.issue;
  if (!issue) return null;
  const Icon = ICON[issue.cta] || CalendarPlus;

  const act = async () => {
    if (issue.cta === 'add_exam') { onOpenChange(false); onAddExam?.(); return; }
    if (issue.cta === 'map_portion') {
      onOpenChange(false);
      if (onMapPortion) onMapPortion(readiness?.exam); else navigate('/exams');
      return;
    }
    if (issue.cta === 'open_exams') { onOpenChange(false); navigate('/exams'); return; }
    if (issue.cta === 'build_plan') {
      setBusy(true);
      try {
        await generatePlan(readiness?.exam?.exam_id);
        toast.success('Your plan is ready');
        onOpenChange(false);
        onReady?.();
      } catch (e) {
        toast.error(e?.message || 'Could not build the plan — open Exam and try there');
      }
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl overflow-hidden pb-24" data-testid="exam-mode-gate">
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">
          {issue.title}
        </DialogTitle>
        <DialogDescription className="font-body text-sm text-slate-600 leading-relaxed">
          {issue.detail}
        </DialogDescription>

        {readiness?.exam && (
          <div className="mt-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"
               data-testid="exam-mode-gate-exam">
            <p className="font-heading text-[13px] text-slate-800">{readiness.exam.name}</p>
            <p className="font-body text-[11px] text-slate-500">
              {readiness.exam.assessment_label}
              {readiness.exam.start_date_ist ? ` · first paper ${readiness.exam.start_date_ist}` : ''}
              {typeof readiness.exam.countdown_days === 'number' && readiness.exam.countdown_days >= 0
                ? ` · ${readiness.exam.countdown_days} days to go` : ''}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button size="sm" onClick={act} disabled={busy} data-testid="exam-mode-gate-cta"
            className="bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white rounded-xl">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Icon className="w-3.5 h-3.5 mr-1.5" />}
            {issue.cta_label}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onBackToExplore?.(); }}
            data-testid="exam-mode-gate-explore" className="rounded-xl">
            <Compass className="w-3.5 h-3.5 mr-1.5" /> Keep exploring for now
          </Button>
        </div>

        <BuddyCorner line={BUDDY_LINE[issue.code] || BUDDY_LINE.no_exam} mood="curious" />
      </DialogContent>
    </Dialog>
  );
}

export default ExamModeGate;
